import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  collection,
  addDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, storage, auth } from "../../firebase";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

// Centralized notes service
import {
  addNote,
  deleteNote as deleteNoteService,
  subscribeToProspectNotes,
} from "../../services/notesService";

import TrackerShell from "../../components/tracker/TrackerShell";
import "../MyTeam/shared-tracker-styles.css";

import { useUIVersion } from "../../contexts/UIVersionContext";
import { useAuth } from "../../hooks/useAuth";
import {
  TrackerContainer,
  TrackerNotes,
  filterByDateRange,
  filterByBaseShop,
} from "../../components/tracker-ui";
import { IconUsers, IconLicense } from "@tabler/icons-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";

/* ===============================
   KPI helpers (computed from rows)
   =============================== */
const KPI_INFO = {
  baseshop: "INFO BUTTON: \nDIRECT POINTS WRITTEN & ISSUED",
  baseshopProj:
    "INFO BUTTON: \nBASESHOP POINTS THAT HAVE BEEN SUBMITTED BUT ARE NOT ISSUED YET",
  personal: "INFO BUTTON: \nDIRECT POINTS WRITTEN & ISSUED",
  personalProj:
    "INFO BUTTON: \nDIRECT POINTS THAT HAVE BEEN SUBMITTED BUT ARE NOT ISSUED YET",
  chargebacks: "INFO BUTTON: \nBUSINESS THAT WAS DECLINED, CANCELLED OR LAPSED",
  npr: "INFO BUTTON: \nNET POINT RATIO = NET POINTS / GROSS POINTS SUBMITTED\n\nNET POINTS = POLICIES THAT ACTUALLY GET ISSUED",
  topProducer:
    "note for developer:\n\nthis should be the agent with the most personal points within the entire baseshop",
};

function rowPointsTotal(row) {
  // Prefer target as the authoritative total; fall back to 40/60 sum if missing
  const target = Number(row?.points?.totalPoints || 0);
  if (!isNaN(target) && target > 0) return target;
  const sixty = Number(row?.points?.sixty || 0);
  const forty = Number(row?.points?.forty || 0);
  return (isNaN(sixty) ? 0 : sixty) + (isNaN(forty) ? 0 : forty);
}
function is40Issued(row) {
  // 40% of points issued when 1st advance date is filled
  return !!row?.advances?.first?.date;
}

function is60Issued(row) {
  // 60% of points issued when 2nd advance date is filled
  return !!row?.advances?.second?.date;
}

function isIssued(row) {
  // Consider a row as "issued" if both advances are filled (for backward compatibility)
  return is40Issued(row) && is60Issued(row);
}

/* ===============================
    ResizableGrid (centered, no lock/sort)
   =============================== */
function ResizableGrid({
  columns,
  rows,
  loading,
  emptyText,
  renderCell,
  rowKey,
  rowStyle,
  renderHeaderSearch,
  sortKey,
  sortDir,
  onSort,
}) {
  const storageKey = "tracker-production-columns";

  const [colWidths, setColWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const savedWidths = JSON.parse(saved);
        const initialWidths = Object.fromEntries(
          columns.map((c) => [c.key, c.width || 140])
        );
        return { ...initialWidths, ...savedWidths };
      }
    } catch (e) {
      console.warn("Failed to load production column widths:", e);
    }
    return Object.fromEntries(columns.map((c) => [c.key, c.width || 140]));
  });

  const saveColumnWidths = useCallback(
    (newWidths) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(newWidths));
      } catch (e) {
        console.warn("Failed to save production column widths:", e);
      }
    },
    [storageKey]
  );

  const dragRef = useRef(null);
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const { key, startX, startW } = dragRef.current;
      const dx = e.clientX - startX;
      const next = Math.max(100, startW + dx);
      setColWidths((w) => ({ ...w, [key]: next }));
    };
    const onUp = () => {
      if (dragRef.current) {
        saveColumnWidths(colWidths);
      }
      dragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [colWidths, saveColumnWidths]);

  const startDrag = (key, e) => {
    dragRef.current = { key, startX: e.clientX, startW: colWidths[key] || 140 };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  return (
    <div className="rg-wrap">
      <div className="rg-scroll">
        {/* Header */}
        <div className="rg-row rg-header">
          {columns.map((c) => {
            const w = colWidths[c.key] || c.width || 140;
            const style = { width: w, minWidth: w, maxWidth: w };
            const sortable = c.sortable;
            const active = sortable && sortKey === c.key;
            const dirSymbol = !sortable
              ? ""
              : active
              ? sortDir === "asc"
                ? "▲"
                : "▼"
              : "⇅";
            return (
              <div
                className={`rg-cell rg-head center ${sortable ? "rg-sortable" : ""} ${active ? "rg-sort-active" : ""}`}
                key={c.key}
                style={style}
                onClick={() => {
                  if (!sortable || !onSort) return;
                  onSort(c.key);
                }}
              >
                <div className="rg-head-inner" title={c.label}>
                  <div className="rg-head-top">
                    <div className="rg-title-group">
                      <span className="rg-title">{c.label}</span>
                      {sortable && (
                        <span className="rg-sort-indicator" aria-hidden="true">
                          {dirSymbol}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="rg-head-search">
                    {renderHeaderSearch ? renderHeaderSearch(c) : null}
                  </div>
                </div>
                <span
                  className="rg-grip"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    startDrag(c.key, e);
                  }}
                  title="Drag to resize"
                />
              </div>
            );
          })}
        </div>

        {/* Body */}
        {loading ? (
          <div className="rg-empty">Loading…</div>
        ) : !rows.length ? (
          <div className="rg-empty">{emptyText || "No data"}</div>
        ) : (
          rows.map((r, rowIdx) => {
            const styleVars =
              (typeof rowStyle === "function" ? rowStyle(r) : {}) || {};
            return (
              <div
                className="rg-row"
                key={rowKey ? rowKey(r) : rowIdx}
                style={styleVars}
              >
                {columns.map((c) => {
                  const w = colWidths[c.key] || c.width || 140;
                  const style = { width: w, minWidth: w, maxWidth: w };
                  return (
                    <div className={`rg-cell center`} key={c.key} style={style}>
                      {renderCell(r, c)}
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>

      {/* Self-contained styles */}
      <style>{`
        .rg-wrap {
          --grid-bg: #0e1116;
          --grid-head-bg: #121621;
          --grid-cell-border: rgba(255,255,255,.06);
          --grid-head-border: rgba(255,255,255,.12);
          --cell-pad-y: 10px;
          --cell-pad-x: 12px;
          border-radius: 10px;
          overflow: hidden;
          background: var(--grid-bg);
        }
        .rg-scroll { overflow: auto; position: relative; }
        .rg-row { display: flex; position: relative; }

        /* Header */
        .rg-header {
          position: sticky; top: 0; z-index: 7;
          border-bottom: 2px solid var(--grid-head-border);
          background: var(--grid-head-bg);
          box-shadow: 0 6px 12px rgba(0,0,0,.25);
        }
        .rg-head {
          background: var(--grid-head-bg);
          color: #eaeef7;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .3px;
          cursor: default;
        }
        .rg-head.rg-sortable { cursor: pointer; }
        .rg-sort-indicator { font-size: 11px; opacity: 0.8; margin-left: 6px; }
        .rg-head.rg-sort-active { color: #ffd666; }
        .rg-head-inner {
          display: flex; flex-direction: column; width: 100%;
          height: 56px; justify-content: center; gap: 6px;
        }
        .rg-head-top { display:flex; align-items:center; justify-content:center; gap:8px; }
        .rg-title-group { min-width: 0; }
        .rg-title {
          display:block; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
          font-size:12px; text-align:center;
        }

        /* Cells */
        .rg-cell {
          display:flex; align-items:center; justify-content:center;
          padding: var(--cell-pad-y) var(--cell-pad-x);
          border-right: 1px solid var(--grid-cell-border);
          box-sizing: border-box;
          background: var(--row-bg, rgba(255,255,255,0.02));
        }
        .rg-row:nth-child(even):not(.rg-header) .rg-cell {
          background: var(--row-bg, rgba(255,255,255,0.03));
        }
        .center * { text-align: center; }
        .rg-head .rg-grip {
          position:absolute; right:0; top:0; bottom:0;
          width:6px; cursor:col-resize; background:transparent;
        }
        .rg-head .rg-grip:hover { background: rgba(255,255,255,.05); }

        .rg-empty { padding:24px; color:#aab1c2; }

        /* Inputs */
        .cell-input, .cell-select, textarea.cell-input {
          width: 100%;
          background: #0d1320;
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 8px;
          padding: 8px 10px;
          color: #e7ecf7;
          outline: none;
          font-size: 14px;
        }
        .cell-input::placeholder { color: #9aa3b4; }
        .cell-select { height: 36px; text-align: center; text-align-last: center; }
        textarea.cell-input { resize: vertical; min-height: 36px; }

        .mini-label { font-size: 11px; opacity: .8; margin-bottom: 4px; }
        .two-col { display:grid; grid-template-columns: 1fr 1fr; gap:8px; width:100%; align-items:center; }
        .three-col { display:grid; grid-template-columns: 1fr 1fr 1fr; gap:8px; width:100%; align-items:center; }

        .actions-inline { display:flex; gap:8px; justify-content:center; width:100%; }
        .icon-btn {
          border: 0; outline: 0; cursor: pointer;
          background: rgba(255,255,255,.1);
          color: #eef3ff;
          padding: 8px 10px; border-radius: 8px;
        }
        .icon-btn.danger { background: #b93b3b; color: #fff; }

        /* Notes modal like 4x4 */
        .notes-btn {
          border: 1px solid rgba(255,255,255,.12);
          background: #0d1320;
          color: #e7ecf7;
          padding: 8px 12px; border-radius: 10px;
          cursor: pointer;
        }
        .notes-dot { width:8px; height:8px; border-radius:50%; background:#2ecc71; display:inline-block; margin-left:6px; }

        /* Checkbox styles */
        .checkbox-container {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .checkbox-input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
        .checkbox-label {
          font-size: 12px;
          color: #e7ecf7;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}

/* ===== Columns ===== */
const STATUS_OPTS = [
  "Complete",
  "Pending",
  "In Progress",
  "Issue",
  "Incomplete",
  "Trial",
  "Chargeback",
];
const DELIVERY_OPTS = [
  "Approved",
  "Issued",
  "PDR",
  "Sent to TFA",
  "Pending",
  "Other",
];
const COMPANY_OPTS = ["TRANSAMERICA", "NATIONWIDE", "NORTH AMERICAN", "EVEREST", "OTHER"];

const PRODUCTS_BY_COMPANY = {
  "TRANSAMERICA": [
    "FFIUL II",
    "TERM LB - 10 YEARS",
    "TERM LB - 15 YEARS",
    "TERM LB - 20/25/30 YEARS",
    "FINAL EXPENSE"
  ],
  "NATIONWIDE": ["NEW HEIGHTS IUL ACCUMULATOR 2020"],
  "NORTH AMERICAN": [
    "SECURE HORIZON - CLIENT AGE 0-70",
    "SECURE HORIZON - CLIENT AGE 71-75",
    "SECURE HORIZON - CLIENT AGE 76+"
  ],
  "EVEREST": ["EVEREST"],
  "OTHER": ["OTHER"]
};

const MULTIPLIER_TABLE = {
  "TRANSAMERICA|FFIUL II": 1.25,
  "TRANSAMERICA|TERM LB - 10 YEARS": 1.10,
  "TRANSAMERICA|TERM LB - 15 YEARS": 1.16,
  "TRANSAMERICA|TERM LB - 20/25/30 YEARS": 1.26,
  "TRANSAMERICA|FINAL EXPENSE": 1.10,
  "NATIONWIDE|NEW HEIGHTS IUL ACCUMULATOR 2020": 1.09,
  "NORTH AMERICAN|SECURE HORIZON - CLIENT AGE 0-70": 0.062888,
  "NORTH AMERICAN|SECURE HORIZON - CLIENT AGE 71-75": 0.053496,
  "NORTH AMERICAN|SECURE HORIZON - CLIENT AGE 76+": 0.040919,
  "EVEREST|EVEREST": 1.00
};

  const SPLIT_OPTS = ["50/50", "70/30"];

// UPDATED COLUMNS ORDER - Corrected as requested
const COLS = [
  { key: "status", label: "POLICY STATUS", type: "status", width: 140, sortable: true },
  { key: "dateWritten", label: "DATE WRITTEN", type: "date", width: 140, sortable: true },
  { key: "closureDate", label: "CLOSURE DATE", type: "date", width: 140, sortable: true },
  { key: "client", label: "CLIENT", type: "text", width: 220, sortable: true },
  { key: "agents", label: "AGENTS", type: "agents", width: 260 },
  {
    key: "points",
    label: "POINTS / 40% / 60%",
    type: "points",
    width: 240,
  },
  {
    key: "advances",
    label: "ADVANCES (1st / 2nd)",
    type: "advances",
    width: 240,
  },
  { key: "policy", label: "POLICY DETAILS", type: "policy", width: 360 },
  { key: "delivery", label: "DELIVERY STATUS", type: "delivery", width: 160, sortable: true },
  { key: "notes", label: "NOTES", type: "notesInline", width: 320 },
  { key: "trialApp", label: "TRIAL APP", type: "trialApp", width: 120, sortable: true },
  { key: "chargeback", label: "CHARGE BACK", type: "chargeback", width: 140 },
  { key: "actions", label: "ACTION", type: "actionsInline", width: 140 },
];

/* ===== Default row shape ===== */
const emptyRow = () => ({
  status: "In Progress",
  trialApp: false,
  chargeback: false,
  chargebackDetails: { deposited12Months: false, deposited26Months: false },
  dateWritten: new Date().toISOString().split("T")[0],
  closureDate: "",
  client: "",
  agents: { agent1: "", agent2: "", split: "" },
  points: { target: "", totalPoints: "", sixty: "", forty: "" },
  advances: {
    first: { date: "", points: "" },
    second: { date: "", points: "" },
  },
  policy: { company: "", product: "", number: "", otherProduct: "" },
  delivery: "Pending",
  notes: "",
  files: [],
});

/* ===== KPI strip (with info tooltips) ===== */
function KpiStrip({ data }) {
  const items = [
    { key: "baseshop", label: "BASESHOP POINTS", info: KPI_INFO.baseshop },
    {
      key: "baseshopProj",
      label: "BASESHOP PROJECTED POINTS",
      info: KPI_INFO.baseshopProj,
    },
    { key: "personal", label: "PERSONAL POINTS", info: KPI_INFO.personal },
    {
      key: "personalProj",
      label: "PERSONAL PROJECTED POINTS",
      info: KPI_INFO.personalProj,
    },
    { key: "chargebacks", label: "CHARGEBACKS", info: KPI_INFO.chargebacks },
    {
      key: "npr",
      label: "NPR",
      info: KPI_INFO.npr,
      format: (v) => (v * 100).toFixed(1) + "%",
    },
    { key: "topProducer", label: "TOP PRODUCER", info: KPI_INFO.topProducer },
  ];

  return (
    <div className="kpis">
      {items.map((i) => {
        const raw = data?.[i.key];
        const val = i.format ? i.format(raw || 0) : raw ?? "—";
        return (
          <div className="kpi" key={i.key}>
            <div className="kpi-title">
              {i.label}
              <span className="kpi-info" title={i.info}>
                {" "}
                ⓘ
              </span>
            </div>
            <div className="kpi-val">{val}</div>
          </div>
        );
      })}
      <style>{`
        .kpis{
          display: grid; 
          grid-template-columns: repeat(7, 1fr); 
          gap: 8px; 
          margin-bottom: 12px;
        }
        .kpi{
          border-radius: 12px; 
          padding: 12px 8px; 
          color: #fff; 
          box-shadow: 0 2px 8px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(255,255,255,.12);
          text-align: center;
          min-height: 75px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          transition: all 0.2s ease;
          background: linear-gradient(135deg, rgba(56, 45, 18, 0.92), rgba(34, 26, 10, 0.92));
          border: 1px solid rgba(243, 214, 122, 0.35);
        }
        .kpi:hover{
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2), inset 0 0 0 1px rgba(255,255,255,.18);
        }
        .kpi-title{
          font-size: 10px; 
          opacity: 0.95; 
          font-weight: 700; 
          letter-spacing: 0.5px;
          line-height: 1.2;
          margin-bottom: 6px;
          text-transform: uppercase;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
        .kpi-info {
          display: inline-block;
          font-weight: 800;
          opacity: 0.9;
          cursor: help;
          user-select: none;
        }
        .kpi-val{
          font-size: 18px; 
          font-weight: 800;
          line-height: 1;
        }
        @media (max-width: 1200px) {
          .kpis{
            grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          }
          .kpi-title{ font-size: 9px; }
          .kpi-val{ font-size: 16px; }
        }
      `}</style>
    </div>
  );
}

/* ===== Notes Cell (4x4-style modal) ===== */
function NotesCell({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const preview = (value || "").trim();
  const has = preview.length > 0;

  return (
    <>
      <button className="notes-btn" onClick={() => setOpen(true)}>
        📝 Notes {has ? <span className="notes-dot" /> : null}
      </button>
      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Edit Notes</div>
            <textarea
              className="cell-input"
              rows={10}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Type notes here…"
            />
            <div className="modal-actions">
              <button className="btn neutral" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed !important; 
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          background: rgba(0,0,0,0.85) !important;
          backdrop-filter: blur(12px) !important;
          -webkit-backdrop-filter: blur(12px) !important;
          display: flex !important; 
          align-items: center !important; 
          justify-content: center !important; 
          z-index: 999999 !important;
          padding: 20px !important;
        }
        .modal-card {
          width: min(780px, 92vw) !important; 
          background: #1a1a1f !important; 
          border: 1px solid rgba(255,255,255,0.15) !important;
          border-radius: 16px !important; 
          padding: 24px !important; 
          box-shadow: 0 20px 60px rgba(0,0,0,0.8) !important;
          max-height: 80vh !important;
          overflow-y: auto !important;
          position: relative !important;
          z-index: 1000000 !important;
        }
        .modal-title { font-weight: 800; margin-bottom: 10px; text-align:center; color:#e7ecf7; }
        .modal-actions { display:flex; gap:10px; justify-content:center; margin-top: 12px; }
        .btn { padding:10px 14px; border-radius:10px; border:0; cursor:pointer; }
        .btn.primary { background:#2ecc71; color:#0b0f18; font-weight:800; }
        .btn.neutral { background:#20283a; color:#e7ecf7; }
      `}</style>
    </>
  );
}

/* ===== Agents Cell (Updated with split options) ===== */
function AgentsCell({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const agents = value || { agent1: "", agent2: "", split: "" };
  const hasData =
    (agents.agent1 || "").trim() ||
    (agents.agent2 || "").trim() ||
    (agents.split || "").trim();

  const handleFieldChange = (field, fieldValue) => {
    onChange({ ...agents, [field]: fieldValue });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="notes-btn">
          👥 Agents {hasData ? <span className="notes-dot" /> : null}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] bg-[#1a1a1f] border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white">Agents</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-1 gap-2">
            <label className="text-sm font-medium text-gray-300">Agent 1</label>
            <input
              className="cell-input"
              value={agents.agent1 || ""}
              onChange={(e) => handleFieldChange("agent1", e.target.value)}
              placeholder="Agent 1 name"
              readOnly
            />
          </div>
          <div className="grid grid-cols-1 gap-2">
            <label className="text-sm font-medium text-gray-300">Agent 2</label>
            <input
              className="cell-input"
              value={agents.agent2 || ""}
              onChange={(e) => handleFieldChange("agent2", e.target.value)}
              placeholder="Agent 2 name"
              readOnly
            />
          </div>
          {/* <div className="grid grid-cols-1 gap-2">
            <label className="text-sm font-medium text-gray-300">
              Split Percentage
            </label>
            <select
              className="cell-select"
              value={agents.split || ""}
              onChange={(e) => handleFieldChange("split", e.target.value)}
              readOnly
            >
              <option value="">Select split</option>
              {SPLIT_OPTS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div> */}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ===== Policy Cell (Updated with company and product options) ===== */
function PolicyCell({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const policy = value || {
    company: "",
    product: "",
    number: "",
    otherProduct: "",
  };
  const hasData =
    (policy.company || "").trim() ||
    (policy.product || "").trim() ||
    (policy.number || "").trim();

  const handleFieldChange = (field, fieldValue) => {
    onChange({ ...policy, [field]: fieldValue });
  };

  // Get available products based on selected company
  const availableProducts = policy.company ? (PRODUCTS_BY_COMPANY[policy.company] || []) : [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="notes-btn">
          📋 Policy {hasData ? <span className="notes-dot" /> : null}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] bg-[#1a1a1f] border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white">Edit Policy Details</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-1 gap-2">
            <label className="text-sm font-medium text-gray-300">Company</label>
            <select
              className="cell-select"
              value={policy.company || ""}
              onChange={(e) => handleFieldChange("company", e.target.value)}
            >
              <option value="">Select company</option>
              {COMPANY_OPTS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <label className="text-sm font-medium text-gray-300">Product</label>
            <select
              className="cell-select"
              value={policy.product || ""}
              onChange={(e) => handleFieldChange("product", e.target.value)}
              disabled={!policy.company}
            >
              <option value="">Select product</option>
              {availableProducts.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          {policy.product === "OTHER" && (
            <div className="grid grid-cols-1 gap-2">
              <label className="text-sm font-medium text-gray-300">
                Other Product
              </label>
              <input
                className="cell-input"
                value={policy.otherProduct || ""}
                onChange={(e) =>
                  handleFieldChange("otherProduct", e.target.value)
                }
                placeholder="Specify other product"
              />
            </div>
          )}
          <div className="grid grid-cols-1 gap-2">
            <label className="text-sm font-medium text-gray-300">
              Policy #
            </label>
            <input
              className="cell-input"
              value={policy.number || ""}
              onChange={(e) => handleFieldChange("number", e.target.value)}
              placeholder="Policy number"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ===== Chargeback Cell ===== */
function ChargebackCell({ value, onChange, rowData, onUpdateDocField }) {
  const [open, setOpen] = useState(false);
  const chargeback = value || false;
  const chargebackDetails = rowData.chargebackDetails || {
    deposited12Months: false,
    deposited26Months: false,
  };

  const handleChargebackChange = (checked) => {
    onChange(checked);
    if (checked) {
      // Auto-set status to Chargeback when checked
      onUpdateDocField(rowData.id, "status", "Chargeback");
    }
  };

  const handleDetailChange = (field, fieldValue) => {
    const updatedDetails = { ...chargebackDetails, [field]: fieldValue };
    onUpdateDocField(rowData.id, "chargebackDetails", updatedDetails);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className="checkbox-container">
          <input
            type="checkbox"
            className="checkbox-input"
            checked={chargeback || false}
            onChange={(e) => handleChargebackChange(e.target.checked)}
          />
          <span className="checkbox-label">Chargeback</span>
        </div>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] bg-[#1a1a1f] border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white">Chargeback Details</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-1 gap-2">
            <label className="text-sm font-medium text-gray-300">
              Chargeback Status
            </label>
            <div className="checkbox-container">
              <input
                type="checkbox"
                className="checkbox-input"
                checked={chargeback || false}
                onChange={(e) => handleChargebackChange(e.target.checked)}
              />
              <span className="checkbox-label">Mark as Chargeback</span>
            </div>
          </div>

          {chargeback && (
            <>
              <div className="text-sm text-gray-400 mt-4 mb-2">
                Chargeback based on timeline (24 months from issue date):
              </div>

              <div className="grid grid-cols-1 gap-2">
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    className="checkbox-input"
                    checked={chargebackDetails.deposited12Months || false}
                    onChange={(e) =>
                      handleDetailChange("deposited12Months", e.target.checked)
                    }
                  />
                  <span className="checkbox-label">
                    Client deposited 12 months or less of target in the 24
                    months policy has been opened (minus 100% target)
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2">
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    className="checkbox-input"
                    checked={chargebackDetails.deposited26Months || false}
                    onChange={(e) =>
                      handleDetailChange("deposited26Months", e.target.checked)
                    }
                  />
                  <span className="checkbox-label">
                    Client deposited 26 months or more of target in the 24
                    months policy has been opened (minus 50% target)
                  </span>
                </div>
              </div>

              <div className="text-xs text-gray-500 mt-2">
                Note: After 24 months if policy is cancelled, it doesn't get a
                chargeback.
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ===============================
   Helper functions for notes
   =============================== */
function uniqueId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `id_${Date.now()}_${Math.round(Math.random() * 1e9)}`;
}

function formatDateTimePretty(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = d
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    .toLowerCase();
  return `${date} at ${time}`;
}

function tsToMs(v) {
  if (!v) return 0;
  if (typeof v?.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

function formatDateTimeISO(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} ${time}`;
}

/* ===== Main Page ===== */
export default function ProductionTracker() {
  const { isFeatureEnabled } = useUIVersion();
  const { currentUser } = useAuth();

  const modernTrackersEnabled = isFeatureEnabled("modernTrackers");
  const modernNotesEnabled = isFeatureEnabled("modernTrackerNotes");
  const modernFiltersEnabled = isFeatureEnabled("modernTrackerFilters");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newProduction, setNewProduction] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  // Filters
  const [dateFilter, setDateFilter] = useState("all");
  const [baseShopFilter, setBaseShopFilter] = useState("all");
  const [customDateRange, setCustomDateRange] = useState({
    start: "",
    end: "",
  });
  const [showNotesModal, setShowNotesModal] = useState(false);
  
  // Notes state
  const [notesOpenFor, setNotesOpenFor] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [focusedNoteInputId, setFocusedNoteInputId] = useState(null);
  const [noteDeletingId, setNoteDeletingId] = useState(null);
  const [notesByEmail, setNotesByEmail] = useState({}); // All notes grouped by prospectId (row.id)

  // Row background by status (updated with new statuses)
  const statusColorMeta = {
    Complete: { bg: "rgba(46, 204, 113, 0.25)" },
    Pending: { bg: "rgba(241, 196, 15, 0.25)" },
    "In Progress": { bg: "rgba(52, 152, 219, 0.25)" },
    Issue: { bg: "rgba(231, 76, 60, 0.25)" },
    Incomplete: { bg: "rgba(230, 126, 34, 0.25)" },
    Trial: { bg: "rgba(241, 196, 15, 0.4)" },
    Chargeback: { bg: "rgba(231, 76, 60, 0.4)" },
  };
  const rowStyle = (r) => {
    if (r.id === "new-production-row")
      return { "--row-bg": "rgba(212,175,55,.12)" };
    const status = r.status || "";
    const meta = statusColorMeta[status];
    return meta ? { "--row-bg": meta.bg } : {};
  };

  // Update functions - moved to main component
  const updateDocField = async (id, field, value) =>
    await updateDoc(doc(db, "trackers", id), {
      [`production.${field}`]: value,
      updatedAt: serverTimestamp(),
    });

  const updateDeep = async (id, path, value) =>
    await updateDoc(doc(db, "trackers", id), {
      [`production.${path.join(".")}`]: value,
      updatedAt: serverTimestamp(),
    });

  const handleSort = useCallback((key) => {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        setSortDir((prevDir) => (prevDir === "asc" ? "desc" : "asc"));
        return prevKey;
      }
      setSortDir("asc");
      return key;
    });
  }, []);

  const removeRow = async (id) => {
    if (window.confirm("Delete this entry?")) {
      // Remove production field from trackers document
      await updateDoc(doc(db, "trackers", id), {
        production: null,
        updatedAt: serverTimestamp(),
      });
    }
  };

  // Notes functions
  const addInlineNote = async (row) => {
    const text = (noteDrafts[row.id] || "").trim();
    if (!text) return;
    try {
      const recruiterName = auth?.currentUser?.displayName || auth?.currentUser?.email || "Me";

      await addNote({
        prospectId: row.id,
        trackerId: row.id,
        emailId: row.id,
        text,
        createdBy: recruiterName,
        tracker: "production",
      });

      // Clear the draft after successful add
      setNoteDrafts((d) => ({ ...d, [row.id]: "" }));
    } catch (e) {
      console.error("Error adding note:", e);
    }
  };

  const deleteNote = async (row, noteId) => {
    if (!row) return;
    setNoteDeletingId(noteId);
    try {
      await deleteNoteService(noteId);
    } catch (e) {
      console.error("Error deleting note:", e);
    } finally {
      setNoteDeletingId(null);
    }
  };

  // Get current notes for modal
  const currentNotesForOpenRow = useMemo(() => {
    if (!notesOpenFor) return [];
    const notes = notesByEmail[notesOpenFor.id] || [];
    return [...notes].sort((a, b) => tsToMs(a.createdAt) - tsToMs(b.createdAt));
  }, [notesOpenFor, notesByEmail]);

  const sortedModalNotes = useMemo(() => {
    const copy = [...currentNotesForOpenRow];
    copy.sort((a, b) => tsToMs(a.createdAt) - tsToMs(b.createdAt));
    return copy;
  }, [currentNotesForOpenRow]);

  // Check closure dates and update status to Incomplete if needed
  useEffect(() => {
    const checkClosureDates = () => {
      const today = new Date().toISOString().split("T")[0];
      rows.forEach((row) => {
        if (
          row.closureDate &&
          row.closureDate <= today &&
          row.status !== "Approved" &&
          row.status !== "Incomplete"
        ) {
          // Update status to Incomplete if closure date has passed and not approved
          updateDocField(row.id, "status", "Incomplete");
        }
      });
    };

    checkClosureDates();
  }, [rows]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "trackers"), (snap) => {
      const list = snap.docs
        .map((d) => {
          const data = d.data();
          // Only include documents that have production data
          if (data.production) {
            return { id: d.id, ...data.production };
          }
          return null;
        })
        .filter(Boolean);
      setRows(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Subscribe to centralized notes for each production row (by prospectId = row.id)
  useEffect(() => {
    const prospectIds = Array.from(new Set(rows.map(r => r.id).filter(Boolean)));
    if (!prospectIds.length) return undefined;

    const unsubscribers = prospectIds.map(prospectId =>
      subscribeToProspectNotes(prospectId, notes => {
        setNotesByEmail(prev => ({
          ...prev,
          [prospectId]: notes,
        }));
      })
    );

    return () => {
      unsubscribers.forEach(unsub => unsub && unsub());
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    let filtered = rows;

    if (modernFiltersEnabled) {
      if (dateFilter !== "all") {
        if (dateFilter === "custom" && customDateRange) {
          filtered = filterByDateRange(
            filtered,
            dateFilter,
            customDateRange,
            "dateWritten"
          );
        } else {
          filtered = filterByDateRange(
            filtered,
            dateFilter,
            null,
            "dateWritten"
          );
        }
      }

      if (baseShopFilter !== "all") {
        filtered = filterByBaseShop(filtered, baseShopFilter, currentUser, {
          recruiterField: "agents.agent1",
          leaderField: "agents.agent2",
          nested: true,
        });
      }
    }

    return filtered;
  }, [
    rows,
    modernFiltersEnabled,
    dateFilter,
    baseShopFilter,
    customDateRange,
    currentUser,
  ]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return filteredRows;
    const dir = sortDir === "desc" ? -1 : 1;
    const getVal = (row) => {
      const v = row?.[sortKey];
      if (sortKey === "dateWritten" || sortKey === "closureDate") {
        const t = new Date(v).getTime();
        return Number.isFinite(t) ? t : -Infinity;
      }
      return (v || "").toString().toLowerCase();
    };
    return [...filteredRows].sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filteredRows, sortKey, sortDir]);

  const finalRows = useMemo(() => {
    const base = sortedRows;
    if (isAddingNew && newProduction) {
      return [{ ...newProduction, id: "new-production-row" }, ...base];
    }
    return base;
  }, [
    sortedRows,
    isAddingNew,
    newProduction,
    rows.length,
    modernFiltersEnabled,
    dateFilter,
    baseShopFilter,
    sortKey,
    sortDir,
  ]);

  // ==== KPI Computation (from filtered rows) - Updated for trial app logic ====
  const computedKpis = useMemo(() => {
    const all = filteredRows;

    // Baseshop totals (includes all rows) - incremental based on advances
    // Add 40% when 1st advance is filled, 60% when 2nd advance is filled
    const nonTrialRows = all.filter((row) => !row.trialApp);
    const baseshopIssued = nonTrialRows.reduce((sum, r) => {
      let pointsToAdd = 0;
      const total = rowPointsTotal(r);
      if (is40Issued(r)) {
        pointsToAdd += total * 0.4;
      }
      if (is60Issued(r)) {
        pointsToAdd += total * 0.6;
      }
      return sum + pointsToAdd;
    }, 0);
    const baseshopSubmitted = nonTrialRows.reduce(
      (sum, r) => sum + rowPointsTotal(r),
      0
    );
    const baseshopProjected = Math.max(baseshopSubmitted - baseshopIssued, 0);

    // Personal totals = policies where current user is Agent 1 (include trial apps for personal)
    const you = (currentUser?.displayName || "").trim().toLowerCase();
    const personalRows = all.filter(
      (r) => (r?.agents?.agent1 || "").trim().toLowerCase() === you
    );
    const personalIssued = personalRows.reduce((s, r) => {
      let pointsToAdd = 0;
      const total = rowPointsTotal(r);
      if (is40Issued(r)) {
        pointsToAdd += total * 0.4;
      }
      if (is60Issued(r)) {
        pointsToAdd += total * 0.6;
      }
      return s + pointsToAdd;
    }, 0);
    const personalSubmitted = personalRows.reduce(
      (s, r) => s + rowPointsTotal(r),
      0
    );
    const personalProjected = Math.max(personalSubmitted - personalIssued, 0);

    // NPR = Net Points / Gross Points Submitted (net = issued)
    const gross = baseshopSubmitted;
    const net = baseshopIssued;
    const npr = gross > 0 ? net / gross : 0;

    // Chargebacks: rows with chargeback true
    const chargebacks = all
      .filter((r) => r.chargeback === true)
      .reduce((s, r) => s + rowPointsTotal(r), 0);

    // Top Producer = agent1 with highest issued (includes all rows)
    const map = new Map();
    nonTrialRows.forEach((r) => {
      const name = (r?.agents?.agent1 || "").trim();
      if (!name) return;
      let add = 0;
      const total = rowPointsTotal(r);
      if (is40Issued(r)) {
        add += total * 0.4;
      }
      if (is60Issued(r)) {
        add += total * 0.6;
      }
      map.set(name, (map.get(name) || 0) + add);
    });
    let topProducer = "—";
    let topVal = 0;
    for (const [name, val] of map.entries()) {
      if (val > topVal) {
        topVal = val;
        topProducer = name;
      }
    }

    return {
      baseshop: baseshopIssued.toFixed(2),
      baseshopProj: baseshopProjected.toFixed(2),
      personal: personalIssued,
      personalProj: personalProjected,
      chargebacks,
      npr,
      topProducer,
    };
  }, [filteredRows, currentUser?.displayName]);

  const handleUpload = async (row, file) => {
    if (!file) return;
    const path = `production/${row.id}/${Date.now()}_${file.name}`;
    const sref = storageRef(storage, path);
    await uploadBytes(sref, file);
    const url = await getDownloadURL(sref);
    const nextFiles = Array.isArray(row.files) ? [...row.files, url] : [url];
    await updateDoc(doc(db, "trackers", row.id), {
      "production.files": nextFiles,
      updatedAt: serverTimestamp(),
    });
    alert("File attached.");
  };

  // Handle trial app logic
  const handleTrialAppChange = async (row, checked) => {
    if (checked) {
      // When trial app is checked, set status to Trial
      await updateDocField(row.id, "trialApp", true);
      await updateDocField(row.id, "status", "Trial");
    } else {
      // When trial app is unchecked
      await updateDocField(row.id, "trialApp", false);
      // If delivery is Approved, set status to Approved
      if (row.delivery === "Approved") {
        await updateDocField(row.id, "status", "Approved");
      }
    }
  };

  // Handle delivery status change
  const handleDeliveryChange = async (row, deliveryValue) => {
    await updateDocField(row.id, "delivery", deliveryValue);

    // If delivery is set to Approved and it's a trial app, uncheck trial app and set status to Approved
    if (deliveryValue === "Approved" && row.trialApp) {
      await updateDocField(row.id, "trialApp", false);
      await updateDocField(row.id, "status", "Approved");
    }

    // If delivery is Approved, set status to Approved
    if (deliveryValue === "Approved") {
      await updateDocField(row.id, "status", "Approved");
    }
  };

  // Calculate points based on target premium and split
  const calculatePoints = (targetPremium, split) => {
    const target = parseFloat(targetPremium) || 0;
    if (split === "70% / 30%") {
      return {
        sixty: (target * 0.7).toFixed(2),
        forty: (target * 0.3).toFixed(2),
      };
    } else if (split === "50% / 50%") {
      return {
        sixty: (target * 0.5).toFixed(2),
        forty: (target * 0.5).toFixed(2),
      };
    } else if (split === "60% / 40%") {
      return {
        sixty: (target * 0.6).toFixed(2),
        forty: (target * 0.4).toFixed(2),
      };
    }
    return { sixty: "", forty: "" };
  };

  // Edit Production Modal
  const EditProductionModal = ({ open, row, onClose, onSave }) => {
    const [form, setForm] = useState(row ? JSON.parse(JSON.stringify(row)) : emptyRow());
    const [agentMode, setAgentMode] = useState(
      row?.agents?.split && row.agents.split !== "100/0" ? "split" : "single"
    );

    useEffect(() => {
      if (row) {
        setForm(JSON.parse(JSON.stringify(row)));
        setAgentMode(
          row?.agents?.split && row.agents.split !== "100/0" ? "split" : "single"
        );
      }
    }, [row]);

    const handleChange = (field, value) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleDeepChange = (path, value) => {
      setForm((prev) => {
        const next = JSON.parse(JSON.stringify(prev));
        let cur = next;
        for (let i = 0; i < path.length - 1; i++) {
          cur = cur[path[i]] ||= {};
        }
        cur[path[path.length - 1]] = value;
        return next;
      });
    };

    const handleAgentModeChange = (mode) => {
      setAgentMode(mode);
      if (mode === "single") {
        handleDeepChange(["agents"], {
          agent1: form.agents?.agent1 || "",
          agent2: "",
          split: "100/0",
        });
      } else {
        handleDeepChange(["agents"], {
          agent1: form.agents?.agent1 || "",
          agent2: form.agents?.agent2 || "",
          split: "50/50",
        });
      }
    };

    const handleSplitChange = (splitValue) => {
      handleDeepChange(["agents", "split"], splitValue);
    };

    const parseSplit = (s) => {
      const [a, b] = (s || "100/0").split("/").map((v) => parseFloat(v) || 0);
      return [a, b];
    };

    const getMultiplier = () => {
      const company = form.policy?.company || "";
      const product = form.policy?.product || "";
      if (company === "OTHER" || product === "OTHER") {
        const customMult = parseFloat(form.policy?.customMultiplier);
        return isNaN(customMult) ? 1 : customMult;
      }
      const key = `${company}|${product}`;
      return MULTIPLIER_TABLE[key] ?? 1;
    };

    const calculatePointsDisplay = () => {
      const base = parseFloat(form.points?.target) || 0;
      const multiplier = getMultiplier();
      const totalPoints = Math.round(base * multiplier * 100) / 100;
      const [pA, pB] = parseSplit(form.agents?.split);

      const pointsA = Math.round(totalPoints * (pA / 100) * 100) / 100;
      const pointsB = Math.round(totalPoints * (pB / 100) * 100) / 100;

      return {
        totalPoints,
        labelA: `${pA}% Points`,
        labelB: `${pB}% Points`,
        pointsA,
        pointsB,
      };
    };

    const points = calculatePointsDisplay();

    const handleSubmit = async () => {
      if (!form.client?.trim() || !form.dateWritten) {
        alert("Please fill in all required fields: Client and Date Written");
        return;
      }
      try {
        // Ensure calculated points are in the form before saving
        const calculatedPoints = calculatePointsDisplay();
        const updatedForm = {
          ...form,
          points: {
            ...form.points,
            totalPoints: calculatedPoints.totalPoints,
            forty: calculatedPoints.pointsA,
            sixty: calculatedPoints.pointsB,
          },
          updatedAt: serverTimestamp()
        };
        
        await updateDoc(doc(db, "trackers", form.id), {
          production: updatedForm,
          updatedAt: serverTimestamp(),
        });
        onClose();
      } catch (error) {
        console.error("Failed to save production:", error);
        alert(`Failed to save: ${error.message}`);
      }
    };

    if (!open || !row) return null;

    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div
          className="modal-card"
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: "90vh", overflowY: "auto", width: "min(900px, 96vw)" }}
        >
          <div className="modal-head">
            <h3>Edit Production</h3>
            <button className="btn ghost" onClick={onClose}>
              ✕
            </button>
          </div>

          <div className="production-form" style={{ padding: "16px 0" }}>
            {/* Basic Info Section */}
            <div className="form-section">
              <h4>Basic Info</h4>
              <div className="form-grid">
                <div className="form-field">
                  <label style={{ fontWeight: 600 }}>Status</label>
                  <select
                    className="cell-select"
                    value={form.status}
                    onChange={(e) => handleChange("status", e.target.value)}
                    style={{ padding: "10px" }}
                  >
                    {STATUS_OPTS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div className="form-field">
                  <label style={{ fontWeight: 600 }}>Date Written</label>
                  <input
                    type="date"
                    className="cell-input"
                    value={form.dateWritten || ""}
                    onChange={(e) => handleChange("dateWritten", e.target.value)}
                    style={{ padding: "10px" }}
                  />
                </div>

                <div className="form-field">
                  <label style={{ fontWeight: 600 }}>Closure Date</label>
                  <input
                    type="date"
                    className="cell-input"
                    value={form.closureDate || ""}
                    onChange={(e) => handleChange("closureDate", e.target.value)}
                    style={{ padding: "10px" }}
                  />
                </div>

                <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                  <label style={{ fontWeight: 600 }}>Client</label>
                  <input
                    type="text"
                    className="cell-input"
                    value={form.client || ""}
                    onChange={(e) => handleChange("client", e.target.value)}
                    placeholder="Client name"
                    style={{ padding: "10px" }}
                  />
                </div>
              </div>
            </div>

            {/* Agents & Share Section */}
            <div className="form-section">
              <h4>Agents & Share</h4>
              <div className="form-grid">
                <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                  <label style={{ fontWeight: 600 }}>Agent Configuration</label>
                  <div style={{ display: "flex", gap: "16px", marginTop: "8px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="agent-mode"
                        value="single"
                        checked={agentMode === "single"}
                        onChange={() => handleAgentModeChange("single")}
                      />
                      <span>Single Agent (100%)</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="agent-mode"
                        value="split"
                        checked={agentMode === "split"}
                        onChange={() => handleAgentModeChange("split")}
                      />
                      <span>Split Agents</span>
                    </label>
                  </div>
                </div>

                <div className="form-field">
                  <label style={{ fontWeight: 600 }}>Agent 1</label>
                  <input
                    type="text"
                    className="cell-input"
                    value={form.agents?.agent1 || ""}
                    onChange={(e) => handleDeepChange(["agents", "agent1"], e.target.value)}
                    placeholder="Agent 1 name"
                    style={{ padding: "10px" }}
                  />
                </div>

                {agentMode === "split" && (
                  <div className="form-field">
                    <label style={{ fontWeight: 600 }}>Agent 2</label>
                    <input
                      type="text"
                      className="cell-input"
                      value={form.agents?.agent2 || ""}
                      onChange={(e) => handleDeepChange(["agents", "agent2"], e.target.value)}
                      placeholder="Agent 2 name"
                      style={{ padding: "10px" }}
                    />
                  </div>
                )}

                {agentMode === "split" && (
                  <div className="form-field">
                    <label style={{ fontWeight: 600 }}>Split</label>
                    <select
                      className="cell-select"
                      value={form.agents?.split || "50/50"}
                      onChange={(e) => handleSplitChange(e.target.value)}
                      style={{ padding: "10px" }}
                    >
                      {SPLIT_OPTS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Policy Details Section */}
            <div className="form-section">
              <h4>Policy Details</h4>
              <div className="form-grid">
                <div className="form-field">
                  <label style={{ fontWeight: 600 }}>Company</label>
                  <select
                    className="cell-select"
                    value={form.policy?.company || ""}
                    onChange={(e) => {
                      handleDeepChange(["policy", "company"], e.target.value);
                      // Clear product when company changes
                      handleDeepChange(["policy", "product"], "");
                      // Recalculate points with new multiplier
                      setTimeout(() => {
                        const newTarget = parseFloat(form.points?.target) || 0;
                        const multiplier = getMultiplier();
                        const newTotal = Math.round(newTarget * multiplier * 100) / 100;
                        handleDeepChange(["points", "totalPoints"], newTotal);
                        handleDeepChange(["points", "forty"], parseFloat((newTotal * 0.4).toFixed(2)));
                        handleDeepChange(["points", "sixty"], parseFloat((newTotal * 0.6).toFixed(2)));
                      }, 0);
                    }}
                    style={{ padding: "10px" }}
                  >
                    <option value="">Select company</option>
                    {COMPANY_OPTS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="form-field">
                  <label style={{ fontWeight: 600 }}>Product</label>
                  <select
                    className="cell-select"
                    value={form.policy?.product || ""}
                    onChange={(e) => {
                      handleDeepChange(["policy", "product"], e.target.value);
                      // Recalculate points with new multiplier
                      setTimeout(() => {
                        const newTarget = parseFloat(form.points?.target) || 0;
                        const multiplier = getMultiplier();
                        const newTotal = Math.round(newTarget * multiplier * 100) / 100;
                        handleDeepChange(["points", "totalPoints"], newTotal);
                        handleDeepChange(["points", "forty"], parseFloat((newTotal * 0.4).toFixed(2)));
                        handleDeepChange(["points", "sixty"], parseFloat((newTotal * 0.6).toFixed(2)));
                      }, 0);
                    }}
                    style={{ padding: "10px" }}
                    disabled={!form.policy?.company}
                  >
                    <option value="">Select product</option>
                    {(form.policy?.company ? (PRODUCTS_BY_COMPANY[form.policy.company] || []) : []).map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>

                {form.policy?.company && form.policy?.product && form.policy?.product !== "OTHER" && (
                  <div className="form-field">
                    <label style={{ fontWeight: 600 }}>Multiplier</label>
                    <input
                      type="text"
                      className="cell-input"
                      value={getMultiplier()}
                      readOnly
                      disabled
                      style={{ padding: "10px", backgroundColor: "rgba(255,255,255,.03)" }}
                    />
                  </div>
                )}

                {(form.policy?.product === "OTHER" || form.policy?.company === "OTHER") && (
                  <div className="form-field">
                    <label style={{ fontWeight: 600 }}>Other Product</label>
                    <input
                      type="text"
                      className="cell-input"
                      value={form.policy?.otherProduct || ""}
                      onChange={(e) => handleDeepChange(["policy", "otherProduct"], e.target.value)}
                      placeholder="Specify product"
                      style={{ padding: "10px" }}
                    />
                  </div>
                )}

                {(form.policy?.product === "OTHER" || form.policy?.company === "OTHER") && (
                  <div className="form-field">
                    <label style={{ fontWeight: 600 }}>Custom Multiplier</label>
                    <input
                      type="number"
                      step="0.01"
                      className="cell-input"
                      value={form.policy?.customMultiplier || ""}
                      onChange={(e) => {
                        handleDeepChange(["policy", "customMultiplier"], e.target.value);
                        // Recalculate points with new multiplier
                        const newTarget = parseFloat(form.points?.target) || 0;
                        const multiplier = parseFloat(e.target.value) || 1;
                        const newTotal = Math.round(newTarget * multiplier * 100) / 100;
                        handleDeepChange(["points", "totalPoints"], newTotal);
                        handleDeepChange(["points", "forty"], parseFloat((newTotal * 0.4).toFixed(2)));
                        handleDeepChange(["points", "sixty"], parseFloat((newTotal * 0.6).toFixed(2)));
                      }}
                      placeholder="Enter multiplier (e.g., 1.25)"
                      style={{ padding: "10px" }}
                    />
                  </div>
                )}

                <div className="form-field">
                  <label style={{ fontWeight: 600 }}>Policy #</label>
                  <input
                    type="text"
                    className="cell-input"
                    value={form.policy?.number || ""}
                    onChange={(e) => handleDeepChange(["policy", "number"], e.target.value)}
                    placeholder="Policy number"
                    style={{ padding: "10px" }}
                  />
                </div>

                <div className="form-field">
                  <label style={{ fontWeight: 600 }}>Delivery</label>
                  <select
                    className="cell-select"
                    value={form.delivery || ""}
                    onChange={(e) => handleChange("delivery", e.target.value)}
                    style={{ padding: "10px" }}
                  >
                    {DELIVERY_OPTS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div className="form-field">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={form.trialApp || false}
                      onChange={(e) => handleChange("trialApp", e.target.checked)}
                    />
                    <span>Trial App</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Points Calculation Section */}
            <div className="form-section">
              <h4>Points Calculation</h4>
              <div className="form-grid">
                <div className="form-field">
                  <label style={{ fontWeight: 600 }}>Target Amount</label>
                  <input
                    type="number"
                    className="cell-input"
                    value={form.points?.target || ""}
                    onChange={(e) => {
                      const newTarget = parseFloat(e.target.value) || 0;
                      handleDeepChange(["points", "target"], newTarget);
                      const multiplier = getMultiplier();
                      const newTotal = Math.round(newTarget * multiplier * 100) / 100;
                      handleDeepChange(["points", "totalPoints"], newTotal);
                      handleDeepChange(["points", "forty"], parseFloat((newTotal * 0.4).toFixed(2)));
                      handleDeepChange(["points", "sixty"], parseFloat((newTotal * 0.6).toFixed(2)));
                    }}
                    placeholder="0"
                    style={{ padding: "10px" }}
                  />
                </div>

                <div className="form-field">
                  <label style={{ fontWeight: 600 }}>Total Points</label>
                  <input
                    type="text"
                    className="cell-input"
                    value={points.totalPoints}
                    readOnly
                    disabled
                    style={{ padding: "10px", backgroundColor: "rgba(255,255,255,.03)" }}
                  />
                </div>

                <div className="form-field">
                  <label style={{ fontWeight: 600 }}>{points.labelA}</label>
                  <input
                    type="text"
                    className="cell-input"
                    value={points.pointsA}
                    readOnly
                    disabled
                    style={{ padding: "10px", backgroundColor: "rgba(255,255,255,.03)" }}
                  />
                </div>

                <div className="form-field">
                  <label style={{ fontWeight: 600 }}>{points.labelB}</label>
                  <input
                    type="text"
                    className="cell-input"
                    value={points.pointsB}
                    readOnly
                    disabled
                    style={{ padding: "10px", backgroundColor: "rgba(255,255,255,.03)" }}
                  />
                </div>
              </div>
            </div>

            {/* Trial App & Chargeback */}
            <div className="form-section">
              <div className="form-grid">
                <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={form.chargeback || false}
                      onChange={(e) => handleChange("chargeback", e.target.checked)}
                    />
                    <span>Chargeback</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", paddingTop: "16px", borderTop: "1px solid rgba(255,255,255,.1)" }}>
            <button className="btn ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn"
              style={{
                background: "rgba(255,215,0,.2)",
                color: "#ffd700",
                borderColor: "rgba(255,215,0,.35)",
              }}
              onClick={handleSubmit}
            >
              Save Changes
            </button>
          </div>

          <style>{`
            .production-form {
              display: flex;
              flex-direction: column;
              gap: 20px;
            }
            .form-section {
              display: flex;
              flex-direction: column;
              gap: 12px;
            }
            .form-section h4 {
              fontWeight: 700;
              fontSize: 14px;
              margin: 0;
              textTransform: uppercase;
              letterSpacing: 0.05em;
              opacity: 0.9;
            }
            .form-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 12px;
            }
            .form-field {
              display: grid;
              gap: 6px;
            }
            .form-field label {
              fontSize: 12px;
              opacity: 0.85;
            }
          `}</style>
        </div>
      </div>
    );
  };

  const renderCell = useCallback(
    (row, col) => {
      const isNew = row.id === "new-production-row";
      const data = isNew ? newProduction : row;

      const handleUpdate = (key, value) => {
        if (isNew) setNewProduction((p) => ({ ...p, [key]: value }));
        else updateDocField(row.id, key, value);
      };
      const handleDeepUpdate = (path, value) => {
        if (isNew) {
          setNewProduction((p) => {
            const next = JSON.parse(JSON.stringify(p));
            let cur = next;
            for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]] ||= {};
            cur[path[path.length - 1]] = value;
            return next;
          });
        } else updateDeep(row.id, path, value);
      };

      switch (col.type) {
        case "status":
          return (
            <select
              className="cell-select"
              value={data.status}
              onChange={(e) => handleUpdate("status", e.target.value)}
            >
              {STATUS_OPTS.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          );

        case "trialApp":
          return (
            <div className="checkbox-container">
              <input
                type="checkbox"
                className="checkbox-input"
                checked={data.trialApp || false}
                onChange={(e) => {
                  if (isNew) {
                    handleUpdate("trialApp", e.target.checked);
                    if (e.target.checked) {
                      handleUpdate("status", "Trial");
                    }
                  } else {
                    handleTrialAppChange(row, e.target.checked);
                  }
                }}
              />
              <span className="checkbox-label">Trial App</span>
            </div>
          );

        case "chargeback":
          return (
            <ChargebackCell
              value={data.chargeback}
              onChange={(value) => handleUpdate("chargeback", value)}
              rowData={data}
              onUpdateDocField={updateDocField}
            />
          );

        case "date":
          return (
            <input
              className="cell-input"
              type="date"
              value={data[col.key] || ""}
              onChange={(e) => handleUpdate(col.key, e.target.value)}
            />
          );

        case "text":
          return (
            <input
              className="cell-input"
              value={data[col.key] || ""}
              onChange={(e) => handleUpdate(col.key, e.target.value)}
              placeholder=""
            />
          );

        case "agents":
          return (
            <AgentsCell
              value={data.agents}
              onChange={(value) => {
                handleUpdate("agents", value);
                // Auto-calculate points when split is selected and target exists
                if (value.split && data.points?.target) {
                  const calculatedPoints = calculatePoints(
                    data.points.target,
                    value.split
                  );
                  handleDeepUpdate(["points", "sixty"], calculatedPoints.sixty);
                  handleDeepUpdate(["points", "forty"], calculatedPoints.forty);
                }
              }}
            />
          );

        case "points": {
          // Display totalPoints, 40% and 60%
          const target = parseFloat(data.points?.target) || 0;
          const totalPoints = parseFloat(data.points?.totalPoints) || Math.round(target * 100) / 100;
          const forty = (totalPoints * 0.4).toFixed(2);
          const sixty = (totalPoints * 0.6).toFixed(2);
          
          return (
            <div className="three-col">
              <div>
                <div className="mini-label">Total Points</div>
                <input
                  className="cell-input"
                  type="number"
                  value={totalPoints || ""}
                  onChange={(e) => {
                    handleDeepUpdate(["points", "target"], e.target.value);
                    // Auto-calculate totalPoints and splits when target changes
                    const newTarget = parseFloat(e.target.value) || 0;
                    const newTotal = Math.round(newTarget * 100) / 100;
                    handleDeepUpdate(["points", "totalPoints"], newTotal);
                    handleDeepUpdate(["points", "forty"], (newTotal * 0.4).toFixed(2));
                    handleDeepUpdate(["points", "sixty"], (newTotal * 0.6).toFixed(2));
                  }}
                  placeholder="0"
                />
              </div>
              <div>
                <div className="mini-label">40%</div>
                <input
                  className="cell-input"
                  type="number"
                  value={forty}
                  readOnly
                  placeholder="0"
                />
              </div>
              <div>
                <div className="mini-label">60%</div>
                <input
                  className="cell-input"
                  type="number"
                  value={sixty}
                  readOnly
                  placeholder="0"
                />
              </div>
            </div>
          );
        }

        case "advances":
          return (
            <div className="two-col">
              <div>
                <div className="mini-label">1st Advance</div>
                <input
                  type="date"
                  className="cell-input"
                  value={data.advances?.first?.date || ""}
                  onChange={(e) =>
                    handleDeepUpdate(
                      ["advances", "first", "date"],
                      e.target.value
                    )
                  }
                />
              </div>
              <div>
                <div className="mini-label">2nd Advance</div>
                <input
                  type="date"
                  className="cell-input"
                  value={data.advances?.second?.date || ""}
                  onChange={(e) =>
                    handleDeepUpdate(
                      ["advances", "second", "date"],
                      e.target.value
                    )
                  }
                />
              </div>
            </div>
          );

        case "policy":
          return (
            <PolicyCell
              value={data.policy}
              onChange={(value) => handleUpdate("policy", value)}
            />
          );

        case "delivery":
          return (
            <select
              className="cell-select"
              value={data.delivery || ""}
              onChange={(e) => {
                if (isNew) {
                  handleUpdate("delivery", e.target.value);
                  if (e.target.value === "Approved") {
                    handleUpdate("status", "Approved");
                    handleUpdate("trialApp", false);
                  }
                } else {
                  handleDeliveryChange(row, e.target.value);
                }
              }}
            >
              {DELIVERY_OPTS.map((d) => (
                <option key={d}>{d}</option>
              ))}
            </select>
          );

        case "notesInline": {
          const rid = row.id;
          const notesArr = notesByEmail[data.id] || [];
          const lastNote =
            notesArr.length > 0
              ? notesArr
                  .slice()
                  .reverse()
                  .find((n) => n.text)
              : null;
          
          const isNoteFocused = focusedNoteInputId === rid;
          
          // Show latest note in the input when not focused, or draft when focused/typing
          const inputValue = isNoteFocused 
            ? (noteDrafts[rid] || "")
            : (noteDrafts[rid] || (lastNote?.text || ""));
          
          return (
            <div className="notes-cell-content">
              <div className="notes-inline-add">
                <input
                  className={`note-input ${!isNoteFocused && lastNote && !noteDrafts[rid] ? "showing-existing" : ""}`}
                  type="text"
                  placeholder={lastNote ? "Add a new note... (Press Enter)" : "Add a quick note... (Press Enter)"}
                  value={inputValue}
                  onChange={(e) =>
                    setNoteDrafts((d) => ({ ...d, [rid]: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (noteDrafts[rid] || "").trim()) {
                      addInlineNote(row);
                    }
                  }}
                  onFocus={() => {
                    setFocusedNoteInputId(rid);
                    // Clear the input when user focuses to type new note
                    if (!noteDrafts[rid]) {
                      setNoteDrafts((d) => ({ ...d, [rid]: "" }));
                    }
                  }}
                  onBlur={() => setFocusedNoteInputId(null)}
                />
                <button
                  className="notes-openall-emoji"
                  onClick={() => setNotesOpenFor(row)}
                  title="Open all notes"
                  type="button"
                >
                  ✏️
                </button>
              </div>
              
              {/* Show note metadata below when showing existing note */}
              {!isNoteFocused && lastNote && !noteDrafts[rid] && (
                <div className="note-meta-under">
                  {lastNote.createdBy || "—"} • {
                    lastNote.createdAtISO
                      ? formatDateTimePretty(lastNote.createdAtISO)
                      : tsToMs(lastNote.createdAt)
                      ? formatDateTimePretty(
                          new Date(tsToMs(lastNote.createdAt)).toISOString()
                        )
                      : ""
                  }
                </div>
              )}
            </div>
          );
        }

        case "actionsInline":
          if (isNew) {
            return (
              <div className="actions-inline">
                <button
                  onClick={async () => {
                    if (!newProduction?.client?.trim()) {
                      alert("Please enter a client name before saving.");
                      return;
                    }
                    // Create new document in trackers collection with production field
                    const newDocRef = doc(collection(db, "trackers"));
                    await setDoc(newDocRef, {
                      production: {
                        ...newProduction,
                        createdAt: serverTimestamp(),
                      },
                      updatedAt: serverTimestamp(),
                    });
                    setIsAddingNew(false);
                    setNewProduction(null);
                  }}
                  className="icon-btn"
                  style={{ background: "#2ecc71", color: "white" }}
                >
                  ✓
                </button>
                <button
                  onClick={() => {
                    setIsAddingNew(false);
                    setNewProduction(null);
                  }}
                  className="icon-btn danger"
                >
                  ✕
                </button>
              </div>
            );
          }
          return (
            <div className="actions-inline">
              <button
                className="icon-btn"
                title="Edit"
                onClick={() => setEditingRow(row)}
                style={{ background: "rgba(66, 165, 245, 0.7)", color: "white" }}
              >
                ✎
              </button>
              <label className="icon-btn" title="Attach">
                📎
                <input
                  type="file"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUpload(row, f);
                    e.target.value = "";
                  }}
                />
              </label>
              <button
                className="icon-btn danger"
                title="Delete"
                onClick={() => removeRow(row.id)}
              >
                🗑️
              </button>
            </div>
          );

        default:
          return <span>{data[col.key]}</span>;
      }
    },
    [newProduction, modernNotesEnabled, updateDocField, updateDeep]
  );

  // Top toolbar
  const ToolbarRight = (
    <button
      className="btn btn-golden"
      onClick={() => {
        setIsAddingNew(true);
        setNewProduction(emptyRow());
      }}
      disabled={isAddingNew}
    >
      + Add New Production
    </button>
  );

  // Licensed count (legacy metric card)
  const licensedCount = useMemo(() => {
    return filteredRows.filter(
      (item) =>
        item.licensed === true ||
        item.status === "licensed" ||
        item.licenseStatus === "active"
    ).length;
  }, [filteredRows]);

  // Legacy metrics and filters bar
  const LegacyMetricsAndFiltersBar = () => (
    <div
      className="production-legacy-bar"
      style={{
        background:
          "linear-gradient(135deg, rgba(26, 26, 31, 0.95) 0%, rgba(18, 18, 18, 0.95) 100%)",
        padding: "16px 20px",
        margin: "0 -20px 16px -20px",
        borderBottom: "1px solid rgba(58, 63, 74, 0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "20px",
        flexWrap: "wrap",
      }}
    >
      {/* Left: metric cards */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div
          className="tracker-metric-card"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "12px 18px",
            borderRadius: "16px",
            background:
              "linear-gradient(135deg, rgba(56, 45, 18, 0.92), rgba(34, 26, 10, 0.92))",
            border: "1px solid rgba(243, 214, 122, 0.45)",
            boxShadow: "0 12px 28px rgba(22, 17, 6, 0.35)",
            color: "#fdf7dc",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "12px",
              background: "rgba(243, 214, 122, 0.22)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#f3d67a",
            }}
          >
            <IconUsers size={20} stroke={1.7} />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              lineHeight: 1.2,
            }}
          >
            <span
              style={{
                fontSize: "11px",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "rgba(253, 244, 210, 0.75)",
              }}
            >
              Families Helped
            </span>
            <span
              style={{
                fontSize: "22px",
                fontWeight: 700,
                letterSpacing: "0.02em",
                color: "#ffe59a",
              }}
            >
              {filteredRows.length}
            </span>
          </div>
        </div>

        {/* <div
          className="tracker-metric-card"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "12px 18px",
            borderRadius: "16px",
            background:
              "linear-gradient(135deg, rgba(56, 45, 18, 0.92), rgba(34, 26, 10, 0.92))",
            border: "1px solid rgba(243, 214, 122, 0.45)",
            boxShadow: "0 12px 28px rgba(22, 17, 6, 0.35)",
            color: "#fdf7dc",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "12px",
              background: "rgba(243, 214, 122, 0.22)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#f3d67a",
            }}
          >
            <IconLicense size={20} stroke={1.7} />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              lineHeight: 1.2,
            }}
          >
            <span
              style={{
                fontSize: "11px",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "rgba(253, 244, 210, 0.75)",
              }}
            >
              Licensed
            </span>
            <span
              style={{
                fontSize: "22px",
                fontWeight: 700,
                letterSpacing: "0.02em",
                color: "#ffe59a",
              }}
            >
              {licensedCount}
            </span>
          </div>
        </div> */}
      </div>

      {/* Right: simple filters */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label
            style={{
              fontSize: "11px",
              color: "rgba(255, 255, 255, 0.7)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            DATE RANGE
          </label>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={{
              background: "rgba(255, 255, 255, 0.08)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              borderRadius: "8px",
              padding: "8px 12px",
              color: "#fff",
              fontSize: "14px",
              minWidth: "120px",
            }}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label
            style={{
              fontSize: "11px",
              color: "rgba(255, 255, 255, 0.7)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            TEAM
          </label>
          <select
            value={baseShopFilter}
            onChange={(e) => setBaseShopFilter(e.target.value)}
            style={{
              background: "rgba(255, 255, 255, 0.08)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              borderRadius: "8px",
              padding: "8px 12px",
              color: "#fff",
              fontSize: "14px",
              minWidth: "120px",
            }}
          >
            <option value="all">All Teams</option>
            <option value="team1">Team 1</option>
            <option value="team2">Team 2</option>
          </select>
        </div>
      </div>
    </div>
  );

  // Choose wrapper component based on UI version
  const TrackerWrapper = modernTrackersEnabled
    ? TrackerContainer
    : TrackerShell;
  const wrapperProps = modernTrackersEnabled
    ? {
        title: "Production Tracker",
        // right: ToolbarRight,
        trackerType: "production",
        totalCount: null,
        showFilters: false,
        filterComponent: null,
      }
    : {
        title: "Production Tracker",
        // right: ToolbarRight,
      };

  return (
    <TrackerWrapper {...wrapperProps}>
      <LegacyMetricsAndFiltersBar />

      <KpiStrip data={computedKpis} />

      {modernNotesEnabled && (
        <TrackerNotes
          userId={currentUser?.uid}
          trackerType="production"
          className="production-notes"
        />
      )}

      <ResizableGrid
        columns={COLS}
        rows={finalRows}
        loading={loading}
        emptyText="No entries."
        renderCell={renderCell}
        rowKey={(r) => r.id}
        rowStyle={rowStyle}
        renderHeaderSearch={() => null}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
      />

      {/* Notes Modal - ProspectTracker style */}
      {notesOpenFor && (
        <div className="modal-backdrop" onClick={() => setNotesOpenFor(null)}>
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <div className="modal-head">
              <h3>Notes — {notesOpenFor.client || "Production Entry"}</h3>
              <button
                className="btn ghost"
                onClick={() => setNotesOpenFor(null)}
              >
                ✕
              </button>
            </div>
            <div className="notes-list-modal">
              {sortedModalNotes.length === 0 && (
                <div className="empty-notes">No notes yet.</div>
              )}
              {sortedModalNotes.map((n) => (
                <div key={n.id} className="note-row-modal">
                  <div className="note-text-modal">{n.text}</div>
                  <div className="note-meta-modal">
                    {n.createdBy || "—"} •{" "}
                    {n.createdAtISO
                      ? formatDateTimeISO(n.createdAtISO)
                      : tsToMs(n.createdAt)
                      ? formatDateTimeISO(
                          new Date(tsToMs(n.createdAt)).toISOString()
                        )
                      : ""}
                  </div>
                  <button
                    className="note-delete-modal"
                    onClick={() => deleteNote(notesOpenFor, n.id)}
                    disabled={noteDeletingId === n.id}
                    title="Delete note"
                  >
                    {noteDeletingId === n.id ? "…" : "Delete"}
                  </button>
                </div>
              ))}
            </div>
            <div className="notes-modal-add">
              <textarea
                className="input"
                rows="2"
                placeholder="Add a new note..."
                value={noteDrafts["modal"] || ""}
                onChange={(e) =>
                  setNoteDrafts((d) => ({ ...d, modal: e.target.value }))
                }
              ></textarea>
              <button
                className="btn primary"
                onClick={async () => {
                  const text = (noteDrafts["modal"] || "").trim();
                  if (!text) return;

                  try {
                    const recruiterName =
                      auth?.currentUser?.displayName ||
                      auth?.currentUser?.email ||
                      "Me";

                    await addNote({
                      prospectId: notesOpenFor.id,
                      trackerId: notesOpenFor.id,
                      emailId: notesOpenFor.id,
                      text,
                      createdBy: recruiterName,
                      tracker: "production",
                    });

                    setNoteDrafts((d) => ({ ...d, modal: "" }));
                  } catch (e) {
                    console.error("Error updating notes:", e);
                  }
                }}
              >
                Add Note
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Production Modal */}
      <EditProductionModal
        open={!!editingRow}
        row={editingRow}
        onClose={() => setEditingRow(null)}
      />

      {/* Page-level styles */}
      <style>{`
        .rg-row .rg-cell { padding-top: 12px; padding-bottom: 12px; }

        .btn-golden {
          background: linear-gradient(135deg, #e5b200, #a88900);
          border: 1px solid rgba(229, 178, 0, 0.6);
          color: #fff;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }
        .btn-golden:hover {
          background: linear-gradient(135deg, #d4a000, #966d00);
          border-color: rgba(229, 178, 0, 0.8);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(229, 178, 0, 0.3);
        }
        .btn-golden:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        /* Notes Cell Styles */
        .notes-cell-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
          width: 100%;
          align-items: flex-start;
        }

        .notes-inline-add {
          display: flex;
          align-items: center;
          gap: 6px;
          width: 100%;
        }

        .note-input {
          flex: 1 1 auto;
          min-width: 120px;
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          padding: 6px 10px;
          transition: all 0.2s ease;
        }

        .note-input.showing-existing {
          background: rgba(255, 215, 0, 0.08);
          border-left: 3px solid rgba(255, 215, 0, 0.4);
          padding-left: 8px;
          font-style: italic;
          opacity: 0.95;
        }

        .notes-openall-emoji {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          padding: 6px 8px;
          cursor: pointer;
        }

        .note-meta-under {
          font-size: 11px;
          opacity: 0.8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Notes Modal Styles - ProspectTracker style */
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-card {
          background: #1f2128;
          color: #fff;
          border: 1px solid #3a3f4a;
          border-radius: 16px;
          box-shadow: 0 14px 40px rgba(0,0,0,.45);
          padding: 16px 16px 12px;
          max-height: 92vh;
          overflow: auto;
          width: min(860px,94vw);
        }
        
        .modal-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 8px;
        }
        
        .modal-body {
          display: block;
        }
        
        .btn {
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.18);
          color: #fff;
          border-radius: 10px;
          padding: 8px 12px;
          cursor: pointer;
        }
        
        .btn.primary {
          background: rgba(255,215,0,.2);
          color: #ffd700;
          border-color: rgba(255,215,0,.35);
        }
        
        .btn.ghost {
          background: transparent;
          border-color: rgba(255,255,255,.25);
        }
        
        .input {
          width: 100%;
          box-sizing: border-box;
          background: rgba(255,255,255,.06);
          color: #fff;
          border: 1px solid rgba(255,255,255,.15);
          border-radius: 10px;
          padding: 10px 12px;
          outline: none;
        }
        
        .input:focus {
          border-color: rgba(255,215,0,.55);
          box-shadow: 0 0 0 2px rgba(255,215,0,.12) inset;
        }
        
        .notes-list-modal {
          max-height: 40vh;
          overflow: auto;
          background: rgba(255,255,255,.04);
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 12px;
          padding: 8px;
          margin-bottom: 12px;
        }
        
        .empty-notes {
          opacity: .7;
          text-align: center;
          padding: 18px 8px;
        }
        
        .note-row-modal {
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: 10px;
          align-items: center;
          padding: 8px;
          border-radius: 10px;
        }
        
        .note-row-modal:not(:last-child) {
          border-bottom: 1px dashed rgba(255,255,255,.08);
        }
        
        .note-text-modal {
          white-space: pre-wrap;
        }
        
        .note-meta-modal {
          opacity: .8;
          font-size: 12px;
        }
        
        .note-delete-modal {
          background: transparent;
          border: 1px dashed rgba(255,100,100,.4);
          color: rgba(255,100,100,.9);
          border-radius: 8px;
          padding: 4px 8px;
          cursor: pointer;
        }
        
        .notes-modal-add {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
      `}</style>
    </TrackerWrapper>
  );
}
